import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';

// Base path for the page's static assets (copied from the v0 source project).
export const IMG = '/img/turo-alternative';

export const BRAND_GREEN = '#33F7BE';

// "Speak with an Expert" CTAs funnel into the transition assessment flow;
// "Book Demo" CTAs open the Google Calendar scheduling page directly.
export const ASSESSMENT_URL = '/transition-plan';
export const DEMO_URL = 'https://calendar.app.google/MrkgHeoJnAkWaWsq8';

const isExternal = (href) => /^https?:\/\//.test(href);

/** Pill CTA — green by default, near-black with tone="dark". */
export const SpeakCta = ({ href = ASSESSMENT_URL, label = 'Speak with an Expert', tone = 'light' }) => {
  const className = `mpa-cta${tone === 'dark' ? ' mpa-cta--dark' : ''}`;
  if (isExternal(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {label}
      </a>
    );
  }
  if (href.startsWith('#')) {
    return (
      <a href={href} className={className}>
        {label}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {label}
    </Link>
  );
};

/** Centered lead-in band above a product card. */
export const LeadIn = ({ eyebrow, title, sub, subLarge }) => (
  <div className="mpa-leadin">
    <p className="mpa-eyebrow">{eyebrow}</p>
    <h2 className="mpa-leadin-title">{title}</h2>
    {sub && <p className={`mpa-leadin-sub${subLarge ? ' mpa-leadin-sub--lg' : ''}`}>{sub}</p>}
  </div>
);

/** Two-column checklist with green check bullets. */
export const FeatureList = ({ items }) => (
  <ul className="mpa-featurelist">
    {items.map((item) => (
      <li key={item}>
        <span className="mpa-check">
          <Check size={12} strokeWidth={3} />
        </span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/** Reveal-on-scroll: returns a ref and whether the element has entered view. */
export const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (
      typeof IntersectionObserver === 'undefined' ||
      (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      setShown(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShown(true);
          io.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, shown };
};

/** Inline style for staggered reveal transitions driven by useReveal. */
export const revealStyle = (shown, delay) => ({
  opacity: shown ? 1 : 0,
  transform: shown ? 'none' : 'translateY(18px)',
  transition:
    'opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)',
  transitionDelay: `${delay}ms`,
});
